import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";

const DOMAINS = ["AI & ML", "Data Science", "Cybersecurity", "Blockchain", "Biotechnology", "Software Engineering", "Computer Vision", "Cloud & DevOps", "Other"];

export default function ProposeProject() {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        title: "", domain: "AI & ML", problem: "", idea: "", outcome: "",
        duration: "3 Months", skills: "", technologies: "",
    });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/proposals", {
                ...form,
                skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
                technologies: form.technologies.split(",").map((s) => s.trim()).filter(Boolean),
            });
            toast.success("Proposal submitted! TechBloom will review feasibility.");
            navigate("/projects");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <DashboardLayout title="Propose Your Project">
            <div className="mx-auto max-w-3xl" data-testid="propose-project-page">
                <div className="flex items-center gap-4">
                    <span className="grid h-12 w-12 place-items-center rounded-2xl bg-secondary/30"><Lightbulb className="h-6 w-6" /></span>
                    <div>
                        <h1 className="font-display text-2xl font-bold tracking-tight">Have an idea? Build your own.</h1>
                        <p className="text-sm text-muted-foreground">Submit your idea — we review feasibility, match a mentor and support you to completion.</p>
                    </div>
                </div>
                <form onSubmit={submit} className="card-soft mt-8 space-y-6 p-8">
                    <div className="grid gap-6 sm:grid-cols-2">
                        <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="title">Project title</Label>
                            <Input id="title" data-testid="proposal-title-input" required value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-lg" />
                        </div>
                        <div className="space-y-2">
                            <Label>Domain</Label>
                            <Select value={form.domain} onValueChange={(v) => set("domain", v)}>
                                <SelectTrigger data-testid="proposal-domain-select" className="rounded-lg"><SelectValue /></SelectTrigger>
                                <SelectContent>{DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Proposed duration</Label>
                            <Select value={form.duration} onValueChange={(v) => set("duration", v)}>
                                <SelectTrigger data-testid="proposal-duration-select" className="rounded-lg"><SelectValue /></SelectTrigger>
                                <SelectContent>{["1 Month", "2 Months", "3 Months", "4 Months", "6 Months"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="problem">Problem statement</Label>
                            <Textarea id="problem" data-testid="proposal-problem-input" required rows={3} value={form.problem} onChange={(e) => set("problem", e.target.value)} className="rounded-lg" />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="idea">Your project idea</Label>
                            <Textarea id="idea" data-testid="proposal-idea-input" required rows={4} value={form.idea} onChange={(e) => set("idea", e.target.value)} className="rounded-lg" />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="outcome">Expected outcome</Label>
                            <Textarea id="outcome" data-testid="proposal-outcome-input" required rows={2} value={form.outcome} onChange={(e) => set("outcome", e.target.value)} className="rounded-lg" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="skills">Your existing skills (comma separated)</Label>
                            <Input id="skills" data-testid="proposal-skills-input" value={form.skills} onChange={(e) => set("skills", e.target.value)} className="rounded-lg" placeholder="Python, SQL" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="tech">Technologies you want to use</Label>
                            <Input id="tech" data-testid="proposal-tech-input" value={form.technologies} onChange={(e) => set("technologies", e.target.value)} className="rounded-lg" placeholder="TensorFlow, React" />
                        </div>
                    </div>
                    <Button data-testid="proposal-submit-button" type="submit" disabled={busy} className="rounded-full px-8">
                        {busy ? "Submitting..." : "Submit Proposal"}
                    </Button>
                </form>
            </div>
        </DashboardLayout>
    );
}
